import { Typography } from "antd";
import classNames from "classnames";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Link } from "../../../../../../../../router/LocaleLink";
import { useSwotScore } from "../../../../../../../../api/services/financial-integration.hooks";
import { is402Error } from "../../../../../../../../api/utils/error.utils";
import { PieChartComponent } from "../../../../../../../components/Charts/PieChart";
import { LockDisplay } from "../../../../../../../components/LockDisplay/LockDisplay";
import { useAuth } from "../../../../../../../../api/auth/useAuth";
import { useIsMobile } from "../../../../../../../../hooks/useIsMobile";
import { PathGenerators } from "../../../../../../../../router/paths";
import CustomSelect, {
  type OptionType,
} from "../../../../../../../components/CustomSelect/CustomSelect";
import classes from "./SwotAnalyzTable.module.css";

type StyleClasses = typeof classes;

const { Text } = Typography;

const COLORS = {
  strengths: "#2563EB",
  weaknesses: "#F59E0B",
  opportunities: "#10B981",
  threats: "#EF4444",
};

type SwotCategory =
  | "strengths"
  | "weaknesses"
  | "opportunities"
  | "threats";

const SWOT_TAB_KEYS: SwotCategory[] = [
  "strengths",
  "weaknesses",
  "opportunities",
  "threats",
];

interface SwotAnalyzTableProps {
  tabAccessToken?: string;
}

function SwotPillTabs({
  classes,
  value,
  onChange,
  tabs,
  ariaLabel,
}: {
  classes: StyleClasses;
  value: SwotCategory;
  onChange: (v: SwotCategory) => void;
  tabs: { key: SwotCategory; label: string }[];
  ariaLabel: string;
}) {
  return (
    <div className={classes.pillTrack} role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={value === tab.key}
          className={classNames(
            classes.pillBtn,
            value === tab.key && classes.pillBtnActive,
          )}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export const SwotAnalyzTable = ({ tabAccessToken }: SwotAnalyzTableProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { symbol } = useParams();
  const [selectedCategory, setSelectedCategory] =
    useState<SwotCategory>("strengths");

  const {
    data: swotData,
    isLoading,
    error,
  } = useSwotScore({
    symbol: symbol || "",
    tabAccessToken,
  });

  const showUpgradeDisplay = error ? is402Error(error) : false;

  const swotTabs = useMemo(
    () =>
      SWOT_TAB_KEYS.map((key) => ({
        key,
        label: t(`pages.topHalal.info.swot.categories.${key}`),
      })),
    [t],
  );

  const categoryOptions: OptionType[] = useMemo(
    () =>
      swotTabs.map((tab) => ({
        label: tab.label,
        value: tab.key,
      })),
    [swotTabs],
  );

  const handleCategoryChange = (value: string | undefined) => {
    if (value) setSelectedCategory(value as SwotCategory);
  };

  const chartData = useMemo(() => {
    const labels = {
      strengths: t("pages.topHalal.info.swot.categories.strengths"),
      weaknesses: t("pages.topHalal.info.swot.categories.weaknesses"),
      opportunities: t("pages.topHalal.info.swot.categories.opportunities"),
      threats: t("pages.topHalal.info.swot.categories.threats"),
    };

    if (!swotData?.data) {
      return [
        { name: labels.strengths, value: 0, color: COLORS.strengths },
        { name: labels.weaknesses, value: 0, color: COLORS.weaknesses },
        { name: labels.opportunities, value: 0, color: COLORS.opportunities },
        { name: labels.threats, value: 0, color: COLORS.threats },
      ];
    }

    const data = swotData.data;
    return [
      {
        name: labels.strengths,
        value: data.strengths ?? 0,
        color: COLORS.strengths,
      },
      {
        name: labels.weaknesses,
        value: data.weaknesses ?? 0,
        color: COLORS.weaknesses,
      },
      {
        name: labels.opportunities,
        value: data.opportunities ?? 0,
        color: COLORS.opportunities,
      },
      { name: labels.threats, value: data.threats ?? 0, color: COLORS.threats },
    ];
  }, [swotData, t]);

  const getCategoryDescriptions = (category: SwotCategory): string[] => {
    if (!swotData?.data) return [];
    switch (category) {
      case "strengths":
        return swotData.data.strengthsDescriptions || [];
      case "weaknesses":
        return swotData.data.weaknessesDescriptions || [];
      case "opportunities":
        return swotData.data.opportunitiesDescriptions || [];
      case "threats":
        return swotData.data.threatsDescriptions || [];
      default:
        return [];
    }
  };

  const descriptions = getCategoryDescriptions(selectedCategory);
  const swotFullUrl = symbol
    ? `${PathGenerators.SYMBOL(symbol)}?tab=6`
    : "?tab=6";

  if (!user) return <LockDisplay height="100%" />;

  if (showUpgradeDisplay) {
    return <LockDisplay status={402} height="100%" />;
  }

  if (isLoading) {
    return (
      <div className={classes.container}>
        <div className={classes.cardHeader}>
          <h3 className={classes.title}>
            {t("pages.topHalal.info.swot.title")}
          </h3>
        </div>
        <div className={classes.loading}>{t("pages.common.loading")}</div>
      </div>
    );
  }

  if (!swotData?.data) {
    return (
      <div className={classes.container}>
        <div className={classes.cardHeader}>
          <h3 className={classes.title}>
            {t("pages.topHalal.info.swot.title")}
          </h3>
        </div>
        <div className={classes.empty}>
          {t("pages.topHalal.info.keyStats.noData")}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.cardHeader}>
        <h3 className={classes.title}>{t("pages.topHalal.info.swot.title")}</h3>
      </div>

      <div className={classes.cardBody}>
        <aside className={classes.chartSide}>
          <div className={classes.chartCluster}>
            <PieChartComponent data={chartData} legen={false} size="compact" />
            <div className={classes.legend}>
              {chartData.map((item, index) => (
                <div key={index} className={classes.legendItem}>
                  <div
                    className={classes.legendDot}
                    style={{ backgroundColor: item.color }}
                  />
                  <Text className={classes.legendText}>{item.name}</Text>
                </div>
              ))}
            </div>
          </div>
        </aside>

        <div className={classes.detailSide}>
          <div className={classes.controls}>
            {isMobile ? (
              <div className={classes.selectContainer}>
                <CustomSelect
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  placeholder={t("pages.topHalal.info.swot.selectCategory")}
                  options={categoryOptions}
                />
              </div>
            ) : (
              <SwotPillTabs
                classes={classes}
                value={selectedCategory}
                onChange={setSelectedCategory}
                tabs={swotTabs}
                ariaLabel={t("pages.topHalal.info.swot.title")}
              />
            )}
          </div>

          <div className={classes.detailBody}>
            <div className={classes.listScroll}>
              {descriptions.length > 0 ? (
                <ul className={classes.bulletList}>
                  {descriptions.slice(0, 4).map((item, index) => (
                    <li key={index} className={classes.bulletItem}>
                      <span
                        className={classes.bulletDot}
                        style={{
                          backgroundColor: COLORS[selectedCategory],
                        }}
                      />
                      <span className={classes.bulletText}>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={classes.emptyLine}>
                  {t("pages.topHalal.info.swot.noCategoryData", {
                    category: swotTabs.find((tab) => tab.key === selectedCategory)
                      ?.label,
                  })}
                </p>
              )}
            </div>
            <div className={classes.cardFooter}>
              <Link to={swotFullUrl} className={classes.viewAllLink}>
                {t("pages.topHalal.info.swot.viewAll")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
